import React, { useState, useEffect } from 'react';
import './FeaturedTab.css';
import useSuperAdminCatalogManagementStore from '@/stores/SuperAdminStore/CatalogManagementStore';
import { FeaturedCourse, ActiveCourse } from '@/services/PagesServices/SuperAdminCatalogService';

const FeaturedTab: React.FC = () => {
  const { featuredCourses, recommendationRules, activeCourses, createFeaturedCourse, toggleFeaturedCourseStatus, fetchActiveCourses, createRecommendationRule, editRecommendationRule, deleteRecommendationRule } = useSuperAdminCatalogManagementStore();

  const [featuredModalOpen, setFeaturedModalOpen] = useState(false);
  const [ruleModalOpen, setRuleModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState<number | null>(null);

  const [featuredFormData, setFeaturedFormData] = useState({
    courseId: '',
    courseInput: '',
  });

  const [ruleFormData, setRuleFormData] = useState({
    rule_name: '',
    rule_condition: '',
    courses: [] as Array<{ id: string; title: string }>,
    courseInput: '',
  });

  const [showCourseDropdown, setShowCourseDropdown] = useState(false);
  const [filteredCourses, setFilteredCourses] = useState<ActiveCourse[]>([]);
  const [showFeaturedCourseDropdown, setShowFeaturedCourseDropdown] = useState(false);
  const [filteredFeaturedCourses, setFilteredFeaturedCourses] = useState<ActiveCourse[]>([]);

  const openFeaturedModal = async () => {
    setFeaturedModalOpen(true);
    setFeaturedFormData({ courseId: '', courseInput: '' });
    try {
      await fetchActiveCourses();
    } catch (error) {
      console.error('Failed to fetch active courses:', error);
    }
  };

  const closeFeaturedModal = () => {
    setFeaturedModalOpen(false);
    setFeaturedFormData({ courseId: '', courseInput: '' });
    setShowFeaturedCourseDropdown(false);
  };

  const handleFeaturedSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!featuredFormData.courseId) {
      alert('Please select a course');
      return;
    }
    try {
      await createFeaturedCourse(parseInt(featuredFormData.courseId));
      closeFeaturedModal();
    } catch (error) {
      console.error('Failed to add featured course:', error);
      alert('Failed to add featured course. Please try again.');
    }
  };

  const openRuleModal = async () => {
    setIsEditMode(false);
    setEditingRuleId(null);
    setRuleModalOpen(true);
    setRuleFormData({ rule_name: '', rule_condition: '', courses: [], courseInput: '' });
    // Fetch active courses when modal opens
    try {
      await fetchActiveCourses();
    } catch (error) {
      console.error('Failed to fetch active courses:', error);
    }
  };

  const openEditRuleModal = async (ruleId: string) => {
    const rule = recommendationRules?.find(r => r.id === ruleId);
    if (!rule) return;

    setIsEditMode(true);
    setEditingRuleId(parseInt(ruleId));
    setRuleModalOpen(true);
    setRuleFormData({
      rule_name: rule.rule_name,
      rule_condition: rule.rule_condition,
      courses: rule.courses,
      courseInput: '',
    });

    try {
      await fetchActiveCourses();
    } catch (error) {
      console.error('Failed to fetch active courses:', error);
    }
  };

  const closeRuleModal = () => {
    setRuleModalOpen(false);
    setRuleFormData({ rule_name: '', rule_condition: '', courses: [], courseInput: '' });
    setShowCourseDropdown(false);
    setIsEditMode(false);
    setEditingRuleId(null);
  };

  const handleRuleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (ruleFormData.courses.length === 0) {
      alert('Please select at least one course for the recommendation rule');
      return;
    }

    try {
      const courseIds = ruleFormData.courses.map(course => parseInt(course.id));

      if (isEditMode && editingRuleId) {
        await editRecommendationRule(
          editingRuleId,
          ruleFormData.rule_name,
          ruleFormData.rule_condition,
          courseIds
        );
      } else {
        await createRecommendationRule(
          ruleFormData.rule_name,
          ruleFormData.rule_condition,
          courseIds
        );
      }

      closeRuleModal();
    } catch (error) {
      console.error(`Failed to ${isEditMode ? 'update' : 'create'} recommendation rule:`, error);
      alert(`Failed to ${isEditMode ? 'update' : 'create'} recommendation rule. Please try again.`);
    }
  };

  const handleDeleteRule = async (ruleId: string) => {
    if (!window.confirm('Are you sure you want to delete this recommendation rule?')) {
      return;
    }

    try {
      await deleteRecommendationRule(parseInt(ruleId));
    } catch (error) {
      console.error('Failed to delete recommendation rule:', error);
      alert('Failed to delete recommendation rule. Please try again.');
    }
  };

  const handleCourseInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setRuleFormData({ ...ruleFormData, courseInput: value });
    setShowCourseDropdown(true);
  };

  const selectCourse = (course: ActiveCourse) => {
    // Check if course is already added
    if (!ruleFormData.courses.find(c => c.id === course.id)) {
      setRuleFormData({
        ...ruleFormData,
        courses: [...ruleFormData.courses, { id: course.id, title: course.title }],
        courseInput: '',
      });
    }
    setShowCourseDropdown(false);
  };

  const removeCourseFromRule = (courseId: string) => {
    setRuleFormData({
      ...ruleFormData,
      courses: ruleFormData.courses.filter(c => c.id !== courseId),
    });
  };

  const handleCourseInputKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setShowCourseDropdown(false);
    }
  };

  const handleFeaturedCourseInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFeaturedFormData({ ...featuredFormData, courseInput: value });
    setShowFeaturedCourseDropdown(true);
  };

  const selectFeaturedCourse = (course: ActiveCourse) => {
    setFeaturedFormData({
      courseId: course.id,
      courseInput: course.title,
    });
    setShowFeaturedCourseDropdown(false);
  };

  const handleFeaturedCourseInputKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setShowFeaturedCourseDropdown(false);
    }
  };

  // Filter courses based on search input for recommendation rules
  useEffect(() => {
    if (activeCourses && ruleFormData.courseInput) {
      const filtered = activeCourses.filter(course =>
        course.title.toLowerCase().includes(ruleFormData.courseInput.toLowerCase())
      );
      setFilteredCourses(filtered);
    } else {
      setFilteredCourses(activeCourses || []);
    }
  }, [activeCourses, ruleFormData.courseInput]);

  // Filter courses based on search input for featured courses
  useEffect(() => {
    if (activeCourses && featuredFormData.courseInput) {
      const filtered = activeCourses.filter(course =>
        course.title.toLowerCase().includes(featuredFormData.courseInput.toLowerCase())
      );
      setFilteredFeaturedCourses(filtered);
    } else {
      setFilteredFeaturedCourses(activeCourses || []);
    }
  }, [activeCourses, featuredFormData.courseInput]);

  const toggleCourseActive = async (courseId: string) => {
    try {
      await toggleFeaturedCourseStatus(parseInt(courseId));
    } catch (error) {
      console.error('Failed to toggle featured course status:', error);
    }
  };

  return (
    <div className="admin-catalog-featured-content">
      <div className="admin-catalog-featured-section">
        <div className="admin-catalog-section-header">
          <div className="admin-catalog-section-title-wrapper">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="admin-catalog-section-icon">
              <path
                d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <h2 className="admin-catalog-section-title">Featured Courses</h2>
          </div>
          <button className="admin-catalog-add-featured-button" onClick={openFeaturedModal}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            Add Featured Course
          </button>
        </div>

        {!featuredCourses || featuredCourses.length === 0 ? (
          <div className="admin-catalog-empty-state">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path
                d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <h3>No Featured Courses Yet</h3>
            <p>Featured courses will be displayed prominently on the learner catalog homepage. Add your first featured course to get started.</p>
          </div>
        ) : (
          <div className="admin-catalog-featured-courses-list">
            {featuredCourses.map((course) => (
              <div key={course.id} className="admin-catalog-featured-course-item">
              <div className="admin-catalog-drag-handle">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M5 9l7 7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <path d="M5 15l7-7 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </div>

              <div className="admin-catalog-course-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2V3zM22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7V3z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>

              <div className="admin-catalog-course-info">
                <h3 className="admin-catalog-course-title">{course.title}</h3>
                <div className="admin-catalog-course-meta">
                  <span className="admin-catalog-course-category">{course.category}</span>
                  <div className="admin-catalog-course-rating">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="#FFA500">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                    <span>{course.rating}</span>
                  </div>
                  <span className="admin-catalog-course-enrolled">{course.enrolled} enrolled</span>
                </div>
              </div>

              <div className="admin-catalog-course-actions">
                <label className="admin-catalog-toggle-switch">
                  <input
                    type="checkbox"
                    checked={course.isActive}
                    onChange={() => toggleCourseActive(course.id)}
                  />
                  <span className="admin-catalog-toggle-slider"></span>
                </label>
              </div>
            </div>
          ))}
          </div>
        )}
      </div>

      <div className="admin-catalog-recommendation-section">
        <div className="admin-catalog-section-header">
          <div className="admin-catalog-section-title-wrapper">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="admin-catalog-section-icon">
              <path
                d="M22 12h-4l-3 9L9 3l-3 9H2"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <h2 className="admin-catalog-section-title">Recommendation Rules</h2>
          </div>
          <button className="admin-catalog-add-rule-button" onClick={openRuleModal}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            Add Rule
          </button>
        </div>

        {!recommendationRules || recommendationRules.length === 0 ? (
          <div className="admin-catalog-empty-state">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path
                d="M22 12h-4l-3 9L9 3l-3 9H2"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <h3>No Recommendation Rules Yet</h3>
            <p>Create smart recommendation rules to automatically suggest relevant courses to learners based on their role, department, tenure, or completion history.</p>
          </div>
        ) : (
          <div className="admin-catalog-rules-list">
            {recommendationRules.map((rule) => (
              <div key={rule.id} className="admin-catalog-rule-item">
              <div className="admin-catalog-rule-header">
                <div className="admin-catalog-rule-title-row">
                  <h3 className="admin-catalog-rule-name">{rule.rule_name}</h3>
                  <span className={`admin-catalog-status-badge admin-catalog-${rule.status.toLowerCase()}`}>
                    {rule.status}
                  </span>
                </div>
                <div className="admin-catalog-rule-actions">
                  <button className="admin-catalog-action-button" aria-label="Edit" onClick={() => openEditRuleModal(rule.id)}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <button className="admin-catalog-action-button" aria-label="Delete" onClick={() => handleDeleteRule(rule.id)}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              <p className="admin-catalog-rule-description">{rule.rule_condition}</p>
              <div className="admin-catalog-rule-tags">
                {rule.courses.map((course, index) => (
                  <span key={index} className="admin-catalog-rule-tag">
                    {course.title}
                  </span>
                ))}
              </div>
            </div>
          ))}
          </div>
        )}
      </div>

      {/* Add Featured Course Modal */}
      {featuredModalOpen && (
        <div className="admin-catalog-modal-overlay" onClick={closeFeaturedModal}>
          <div className="admin-catalog-modal-content admin-catalog-featured-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-catalog-modal-header">
              <div className="admin-catalog-modal-header-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h2 className="admin-catalog-modal-title">Add Featured Course</h2>
              <button className="admin-catalog-modal-close" onClick={closeFeaturedModal}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleFeaturedSubmit}>
              <div className="admin-catalog-form-group">
                <label htmlFor="featuredCourse" className="admin-catalog-form-label">
                  Select Course <span className="admin-catalog-required">*</span>
                </label>
                <div className="admin-catalog-course-input-wrapper" style={{ position: 'relative' }}>
                  <input
                    type="text"
                    id="featuredCourse"
                    className="admin-catalog-form-input"
                    placeholder="Search for courses..."
                    value={featuredFormData.courseInput}
                    onChange={handleFeaturedCourseInputChange}
                    onKeyDown={handleFeaturedCourseInputKeyPress}
                    onFocus={() => setShowFeaturedCourseDropdown(true)}
                    required
                  />
                  <button
                    type="button"
                    className="admin-catalog-add-course-btn"
                    onClick={() => setShowFeaturedCourseDropdown(!showFeaturedCourseDropdown)}
                    style={{ backgroundColor: 'white', border: '1px solid #e0e0e0' }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M6 9l6 6 6-6" stroke="#000000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>

                  {showFeaturedCourseDropdown && filteredFeaturedCourses.length > 0 && (
                    <div
                      className="admin-catalog-course-dropdown"
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        maxHeight: '200px',
                        overflowY: 'auto',
                        backgroundColor: 'white',
                        border: '1px solid #e0e0e0',
                        borderRadius: '4px',
                        marginTop: '4px',
                        zIndex: 1000,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }}
                    >
                      {filteredFeaturedCourses.map((course) => (
                        <div
                          key={course.id}
                          className="admin-catalog-course-dropdown-item"
                          onClick={() => selectFeaturedCourse(course)}
                          style={{
                            padding: '10px 12px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #f5f5f5',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                        >
                          <div style={{ fontWeight: 500, fontSize: '14px', color: '#333' }}>
                            {course.title}
                          </div>
                          {course.description && (
                            <div style={{ fontSize: '12px', color: '#666', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {course.description}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="admin-catalog-info-box">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                  <path d="M12 8h.01M12 12v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <div>
                  <strong>Featured Course Settings</strong>
                  <p>Featured courses will be displayed prominently on the learner catalog homepage. Use the order controls to arrange them.</p>
                </div>
              </div>

              <div className="admin-catalog-modal-actions">
                <button type="button" className="admin-catalog-btn-cancel" onClick={closeFeaturedModal}>
                  Cancel
                </button>
                <button type="submit" className="admin-catalog-btn-save">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M17 21v-8H7v8M7 3v5h8"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Add Course
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Recommendation Rule Modal */}
      {ruleModalOpen && (
        <div className="admin-catalog-modal-overlay" onClick={closeRuleModal}>
          <div className="admin-catalog-modal-content admin-catalog-rule-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-catalog-modal-header">
              <div className="admin-catalog-modal-header-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M22 12h-4l-3 9L9 3l-3 9H2"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h2 className="admin-catalog-modal-title">{isEditMode ? 'Edit Recommendation Rule' : 'Create Recommendation Rule'}</h2>
              <button className="admin-catalog-modal-close" onClick={closeRuleModal}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleRuleSubmit}>
              <div className="admin-catalog-form-group">
                <label htmlFor="ruleName" className="admin-catalog-form-label">
                  Rule Name <span className="admin-catalog-required">*</span>
                </label>
                <input
                  type="text"
                  id="ruleName"
                  className="admin-catalog-form-input"
                  placeholder="e.g., New Joiners Onboarding"
                  value={ruleFormData.rule_name}
                  onChange={(e) => setRuleFormData({ ...ruleFormData, rule_name: e.target.value })}
                  required
                />
              </div>

              <div className="admin-catalog-form-group">
                <label htmlFor="ruleCondition" className="admin-catalog-form-label">
                  Condition <span className="admin-catalog-required">*</span>
                </label>
                <input
                  type="text"
                  id="ruleCondition"
                  className="admin-catalog-form-input"
                  placeholder="e.g., Show to employees with tenure < 30 days"
                  value={ruleFormData.rule_condition}
                  onChange={(e) => setRuleFormData({ ...ruleFormData, rule_condition: e.target.value })}
                  required
                />
                <p className="admin-catalog-form-hint">Describe when this rule should trigger. This is for documentation purposes.</p>
              </div>

              <div className="admin-catalog-form-group">
                <label htmlFor="recommendedCourses" className="admin-catalog-form-label">
                  Recommended Courses <span className="admin-catalog-required">*</span>
                </label>
                <div className="admin-catalog-course-input-wrapper" style={{ position: 'relative' }}>
                  <input
                    type="text"
                    id="recommendedCourses"
                    className="admin-catalog-form-input"
                    placeholder="Search for courses..."
                    value={ruleFormData.courseInput}
                    onChange={handleCourseInputChange}
                    onKeyDown={handleCourseInputKeyPress}
                    onFocus={() => setShowCourseDropdown(true)}
                  />
                  <button
                    type="button"
                    className="admin-catalog-add-course-btn"
                    onClick={() => setShowCourseDropdown(!showCourseDropdown)}
                    style={{ backgroundColor: 'white', border: '1px solid #e0e0e0' }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M6 9l6 6 6-6" stroke="#000000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>

                  {/* Dropdown */}
                  {showCourseDropdown && filteredCourses.length > 0 && (
                    <div
                      className="admin-catalog-course-dropdown"
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        maxHeight: '200px',
                        overflowY: 'auto',
                        backgroundColor: 'white',
                        border: '1px solid #e0e0e0',
                        borderRadius: '4px',
                        marginTop: '4px',
                        zIndex: 1000,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }}
                    >
                      {filteredCourses.map((course) => (
                        <div
                          key={course.id}
                          className="admin-catalog-course-dropdown-item"
                          onClick={() => selectCourse(course)}
                          style={{
                            padding: '10px 12px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #f5f5f5',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                        >
                          <div style={{ fontWeight: 500, fontSize: '14px', color: '#333' }}>
                            {course.title}
                          </div>
                          {course.description && (
                            <div style={{ fontSize: '12px', color: '#666', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {course.description}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {ruleFormData.courses.length > 0 && (
                  <div className="admin-catalog-added-courses">
                    {ruleFormData.courses.map((course, index) => (
                      <span key={index} className="admin-catalog-course-tag">
                        {course.title}
                        <button
                          type="button"
                          onClick={() => removeCourseFromRule(course.id)}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="admin-catalog-smart-recommendations-box">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <div>
                  <strong>Smart Recommendations</strong>
                  <p>Recommendation rules automatically suggest relevant courses to learners based on conditions like role, department, tenure, or completion history.</p>
                </div>
              </div>

              <div className="admin-catalog-modal-actions">
                <button type="button" className="admin-catalog-btn-cancel" onClick={closeRuleModal}>
                  Cancel
                </button>
                <button type="submit" className="admin-catalog-btn-save">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M17 21v-8H7v8M7 3v5h8"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {isEditMode ? 'Update Rule' : 'Create Rule'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeaturedTab;
